import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'react-bootstrap'
import DatePicker from "react-datepicker"
import Select from 'react-select'
import XLSX from 'xlsx'

import FilterSearch from 'pages/parts/FilterSearch'
import CustomTooltip from "pages/parts/CustomTooltip"
import { httpOk, GET } from 'helpers/api'
import { formatMoney, returnSign, formatDate } from 'helpers/helpers'

function RollProfit() {
	const { t } = useTranslation()
	const date = new Date()

	const [data, setData] = useState({ data: [] })
	const [rolls, setRolls] = useState([])
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [details, setDetails] = useState({})
	const [openRolls, setOpenRolls] = useState({})
	const [openBatches, setOpenBatches] = useState({})
	const [sortField, setSortField] = useState('profit')
	const [sortDir, setSortDir] = useState('desc')

	const [filterData, setFilterData] = useState({
		start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
		end_date: formatDate(new Date(), 'yyyy-MM-dd'),
		roll_id: null,
		search: '',
	})

	function buildUrlParams() {
		let p = ""
		if (filterData.roll_id) p += returnSign(p) + 'roll_id=' + filterData.roll_id.product_id
		if (filterData.start_date) p += returnSign(p) + 'start_date=' + filterData.start_date
		if (filterData.end_date) p += returnSign(p) + 'end_date=' + filterData.end_date
		if (filterData.search) p += returnSign(p) + 'search=' + encodeURIComponent(filterData.search)
		return p
	}

	async function searchWithFilter() {
		const urlParams = buildUrlParams()
		if (urlParams !== "") setFilterDataExist(true)
		setOpenRolls({})
		setOpenBatches({})
		setDetails({})
		const response = await GET('/report/roll-profit' + urlParams, {}, { loader: true })
		if (httpOk(response)) setData(response.data)
		setDropdownIsOpen(false)
	}

	async function toggleRoll(elementId) {
		const isOpen = !!openRolls[elementId]
		setOpenRolls({ ...openRolls, [elementId]: !isOpen })
		if (!isOpen && !details[elementId]) {
			setDetails(d => ({ ...d, [elementId]: { loading: true, batches: [] } }))
			let p = "?element_id=" + elementId
			if (filterData.start_date) p += '&start_date=' + filterData.start_date
			if (filterData.end_date) p += '&end_date=' + filterData.end_date
			const response = await GET('/report/roll-profit-detail' + p, {}, { loader: false })
			if (httpOk(response)) {
				setDetails(d => ({ ...d, [elementId]: { loading: false, batches: response.data.data || [] } }))
			} else {
				setDetails(d => ({ ...d, [elementId]: { loading: false, batches: [] } }))
			}
		}
	}

	function toggleBatch(key) {
		setOpenBatches(o => ({ ...o, [key]: !o[key] }))
	}

	function resetFilter() {
		setFilterData({
			start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
			end_date: formatDate(new Date(), 'yyyy-MM-dd'),
			roll_id: null,
			search: '',
		})
		setFilterDataExist(false)
	}

	async function getMenu() {
		const response = await GET('/helper/rolls', {}, { loader: true })
		if (httpOk(response)) setRolls(response.data)
	}

	useEffect(() => { getMenu() }, []) // eslint-disable-line react-hooks/exhaustive-deps
	useEffect(() => { searchWithFilter() }, [filterData.search]) // eslint-disable-line react-hooks/exhaustive-deps

	function markup(profit, cost) { return cost ? (profit / cost * 100).toFixed(1) : '0.0' }
	function margin(profit, sales) { return sales ? (profit / sales * 100).toFixed(1) : '0.0' }
	function perMeter(profit, metr) { return metr ? profit / metr : 0 }
	function daysClass(d) { return d === null || d === undefined ? '' : (d <= 12 ? 'text-success' : (d >= 18 ? 'text-danger' : '')) }
	function batchId(b) {
		if (b.document_number) return `№${b.document_number}`
		if (b.document_in_id) return `ID ${b.document_in_id}`
		return `#${b.balance_id}`
	}

	const totalProfit = data.total_profit || 0
	const totalMarkup = markup(totalProfit, data.total_cost || 0)
	const totalMargin = margin(totalProfit, data.total_sales || 0)
	function profitShare(profit) { return totalProfit ? (profit / totalProfit * 100) : 0 }

	// Saralash
	function handleSort(field) {
		if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
		else { setSortField(field); setSortDir('desc') }
	}
	function sortArrow(field) {
		if (sortField !== field) return <i className="uil uil-angle-up-b text-muted" style={{ opacity: .3 }}></i>
		return <i className={`uil ${sortDir === 'asc' ? 'uil-angle-up-b' : 'uil-angle-down'}`}></i>
	}
	function sortValue(item, field) {
		switch (field) {
			case 'name': return (item.element_name || '').toLowerCase()
			case 'tonna': return +item.total_tonna || 0
			case 'metr': return +item.total_metr || 0
			case 'batch': return +item.batch_count || 0
			case 'sales': return +item.total_sales || 0
			case 'cost': return +item.total_cost || 0
			case 'share':
			case 'profit': return +item.profit || 0
			case 'perm': return perMeter(item.profit, item.total_metr)
			case 'markup': return item.total_cost ? item.profit / item.total_cost : 0
			case 'margin': return item.total_sales ? item.profit / item.total_sales : 0
			case 'days': return +item.avg_days || 0
			default: return 0
		}
	}
	const sortedRows = [...(data.data || [])].sort((a, b) => {
		const av = sortValue(a, sortField), bv = sortValue(b, sortField)
		if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
		return sortDir === 'asc' ? av - bv : bv - av
	})

	function exportExcel() {
		const header = ['#', t('roll'), t('tonna'), t('metr'), t('batch_count'), t('sales'), t('cost_price'),
			t('gross_income'), t('profit_share'), t('per_meter_profit'), t('markup'), t('margin'), t('avg_selling_days')]
		const aoa = [header]
		sortedRows.forEach((item, i) => {
			aoa.push([
				i + 1,
				item.element_name,
				Number((+item.total_tonna || 0).toFixed(2)),
				Math.round(+item.total_metr || 0),
				item.batch_count,
				Math.round(+item.total_sales || 0),
				Math.round(+item.total_cost || 0),
				Math.round(+item.profit || 0),
				Number(profitShare(item.profit).toFixed(1)),
				Math.round(perMeter(item.profit, item.total_metr)),
				Number(markup(item.profit, item.total_cost)),
				Number(margin(item.profit, item.total_sales)),
				Math.round(+item.avg_days || 0),
			])
		})
		const ws = XLSX.utils.aoa_to_sheet(aoa)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS")
		XLSX.writeFile(wb, t('roll_profit') + ".xlsx")
	}

	const Th = ({ field, label, cls }) => (
		<th className={`${cls || 'text-end'}`} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort(field)}>
			{label} {sortArrow(field)}
		</th>
	)

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">
					{t('roll_profit')} [{data?.data?.length || 0}]
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<FilterSearch filterData={filterData} setFilterData={setFilterData}></FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>
									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('roll')}</label>
												<Select
													isClearable
													options={rolls}
													value={filterData.roll_id}
													onChange={(option) => setFilterData({ ...filterData, roll_id: option || null })}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name}
													getOptionValue={(option) => option.product_id}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('period')}</label>
												<div className="calendar-input">
													<DatePicker
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.start_date ? new Date(filterData.start_date) : ''}
														onChange={(d) => setFilterData({ ...filterData, start_date: formatDate(d, 'yyyy-MM-dd') })} />
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
											<div className="form-group col-md-6">
												<label className="invisible">.</label>
												<div className="calendar-input">
													<DatePicker
														className="form-control"
														dateFormat="dd.MM.yyyy"
														selected={filterData.end_date ? new Date(filterData.end_date) : ''}
														onChange={(d) => setFilterData({ ...filterData, end_date: formatDate(d, 'yyyy-MM-dd') })} />
													<i className="uil uil-calendar-alt"></i>
												</div>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary" onClick={() => setDropdownIsOpen(false)}>{t('cancel')}</button>
													<button className="btn btn-primary" onClick={() => searchWithFilter()}>{t('apply')}</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
							<div>
								<CustomTooltip text={t('EXCEL')}>
									<button className="btn btn-outline-primary btn-wide-2" onClick={exportExcel}>
										<i className="uil uil-file-download-alt"></i>
									</button>
								</CustomTooltip>
							</div>
						</div>
						<div className="vertical-center" style={{ width: '320px' }}>
							<h6 className="d-flex justify-content-between">
								<b>{t('sales')}:</b>
								<span>{formatMoney(data?.total_sales)}</span>
							</h6>
							<h6 className="d-flex justify-content-between">
								<b>{t('cost_price')}:</b>
								<span>{formatMoney(data?.total_cost)}</span>
							</h6>
							<h6 className="d-flex justify-content-between">
								<b>{t('gross_income')}:</b>
								<span>{formatMoney(data?.total_profit)}</span>
							</h6>
							<h6 className="d-flex justify-content-between text-muted">
								<b>{t('markup')} / {t('margin')}:</b>
								<span>{totalMarkup}% / {totalMargin}%</span>
							</h6>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong><br />
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date && <span>{formatDate(filterData.start_date)}</span>}
										{(filterData.start_date && filterData.end_date) && <span>|</span>}
										{filterData.end_date && <span>{formatDate(filterData.end_date)}</span>}
									</span>
								}
								{filterData.roll_id &&
									<span className="me-2"><b>{t('roll')}: </b>{filterData.roll_id.name}</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					{data.excluded_count > 0 &&
						<div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: '13px' }}>
							<b>⚠️ {data.excluded_count} ta tannarxsiz (soxta) rulon chiqarildi</b>
							{data.excluded_names && data.excluded_names.length > 0 &&
								<span> ({data.excluded_names.join(', ')})</span>
							}
							<span> — sotuv {formatMoney(data.excluded_sales)}, soxta "foyda" {formatMoney(data.excluded_profit)} so'm. Bu rulonlarning tannarxi kiritilmagan; sotuvda real rulonni tanlang.</span>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ width: '33px' }} className="text-center">#</th>
									<Th field="name" label={t('roll')} cls="text-start" />
									<Th field="tonna" label={t('tonna')} />
									<Th field="metr" label={t('metr')} />
									<Th field="batch" label={t('batch_count')} />
									<Th field="sales" label={t('sales')} />
									<Th field="cost" label={t('cost_price')} />
									<Th field="profit" label={t('gross_income')} />
									<Th field="share" label={t('profit_share')} />
									<Th field="perm" label={t('per_meter_profit')} />
									<Th field="markup" label={t('markup')} />
									<Th field="margin" label={t('margin')} />
									<Th field="days" label={t('avg_selling_days')} />
								</tr>
							</thead>
							<tbody>
								{sortedRows.length === 0 &&
									<tr><td colSpan={13} className="text-center text-muted py-4">{t('list_empty')}</td></tr>
								}
								{sortedRows.map((item, index) => {
									const isOpen = !!openRolls[item.element_id]
									const det = details[item.element_id]
									return (
										<React.Fragment key={item.element_id}>
											<tr style={{ cursor: 'pointer' }} onClick={() => toggleRoll(item.element_id)}>
												<td className="text-center">{index + 1}</td>
												<td>
													<i className={`uil ${isOpen ? 'uil-angle-down' : 'uil-angle-right'} me-1`}></i>
													<b>{item.element_name}</b>
												</td>
												<td className="text-end">{formatMoney(item.total_tonna, 2)}</td>
												<td className="text-end">{formatMoney(item.total_metr, 1)}</td>
												<td className="text-end"><span className="badge bg-primary">{item.batch_count}</span></td>
												<td className="text-end">{formatMoney(item.total_sales)}</td>
												<td className="text-end">{formatMoney(item.total_cost)}</td>
												<td className="text-end text-success fw-bold">{formatMoney(item.profit)}</td>
												<td className="text-end fw-semibold">{profitShare(item.profit).toFixed(1)}%</td>
												<td className="text-end text-success">{formatMoney(perMeter(item.profit, item.total_metr))}</td>
												<td className="text-end text-success">{markup(item.profit, item.total_cost)}%</td>
												<td className="text-end text-success">{margin(item.profit, item.total_sales)}%</td>
												<td className={`text-end fw-semibold ${daysClass(Math.round(item.avg_days))}`}>
													{Math.round(item.avg_days)} {t('days_short')}
												</td>
											</tr>

											{isOpen && det && det.loading &&
												<tr className="bg-light"><td colSpan={13} className="text-center text-muted py-2">...</td></tr>
											}
											{isOpen && det && !det.loading && det.batches.length === 0 &&
												<tr className="bg-light"><td colSpan={13} className="text-center text-muted py-2">{t('list_empty')}</td></tr>
											}
											{isOpen && det && !det.loading && det.batches.map((b) => {
												const bkey = item.element_id + '_' + b.balance_id
												const bOpen = !!openBatches[bkey]
												return (
													<React.Fragment key={b.balance_id}>
														<tr className="bg-light" style={{ cursor: 'pointer' }} onClick={() => toggleBatch(bkey)}>
															<td></td>
															<td style={{ paddingLeft: '26px' }}>
																<i className={`uil ${bOpen ? 'uil-angle-down' : 'uil-angle-right'} me-1`}></i>
																<b>{b.receipt_date ? formatDate(b.receipt_date) : '—'}</b>
																<span className="ms-2" style={{ fontSize: '12px' }}>{batchId(b)}</span>
																{b.supplier_name &&
																	<span className="text-muted ms-2" style={{ fontSize: '12px' }}>· {b.supplier_name}</span>
																}
															</td>
															<td className="text-end">{formatMoney(b.tonna, 2)}</td>
															<td className="text-end">{formatMoney(b.metr, 1)}</td>
															<td></td>
															<td className="text-end">{formatMoney(b.sales)}</td>
															<td className="text-end">{formatMoney(b.cost)}</td>
															<td className="text-end text-success fw-bold">{formatMoney(b.profit)}</td>
															<td className="text-end">{profitShare(b.profit).toFixed(1)}%</td>
															<td className="text-end text-success">{formatMoney(perMeter(b.profit, b.metr))}</td>
															<td className="text-end text-success">{markup(b.profit, b.cost)}%</td>
															<td className="text-end text-success">{margin(b.profit, b.sales)}%</td>
															<td className={`text-end ${daysClass(b.days)}`}>
																{b.days !== null && b.days !== undefined ? `${b.days} ${t('days_short')}` : '—'}
															</td>
														</tr>
														{bOpen && b.types.map((tp, ti) => (
															<tr key={ti} className="text-muted" style={{ fontSize: '13px' }}>
																<td></td>
																<td style={{ paddingLeft: '52px' }}>{tp.type_name}</td>
																<td className="text-end">{formatMoney(tp.tonna, 2)}</td>
																<td className="text-end">{formatMoney(tp.metr, 1)}</td>
																<td></td>
																<td className="text-end">{formatMoney(tp.sales)}</td>
																<td className="text-end">{formatMoney(tp.cost)}</td>
																<td className="text-end">{formatMoney(tp.profit)}</td>
																<td></td>
																<td className="text-end">{formatMoney(perMeter(tp.profit, tp.metr))}</td>
																<td className="text-end">{markup(tp.profit, tp.cost)}%</td>
																<td className="text-end">{margin(tp.profit, tp.sales)}%</td>
																<td></td>
															</tr>
														))}
													</React.Fragment>
												)
											})}
										</React.Fragment>
									)
								})}
							</tbody>
							<tfoot>
								<tr>
									<td></td>
									<td><b>{t('total')}</b></td>
									<td className="text-end"><b>{formatMoney(data.total_tonna, 2)}</b></td>
									<td className="text-end"><b>{formatMoney(data.total_metr, 1)}</b></td>
									<td></td>
									<td className="text-end"><b>{formatMoney(data.total_sales)}</b></td>
									<td className="text-end"><b>{formatMoney(data.total_cost)}</b></td>
									<td className="text-end text-success"><b>{formatMoney(data.total_profit)}</b></td>
									<td className="text-end"><b>100%</b></td>
									<td></td>
									<td className="text-end text-success"><b>{totalMarkup}%</b></td>
									<td className="text-end text-success"><b>{totalMargin}%</b></td>
									<td></td>
								</tr>
							</tfoot>
						</table>
					</div>
				</div>
			</div>
		</>
	)
}

export default RollProfit
